import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {PagingDto} from 'features/core/shared/dto/model/paging.dto';
import {Sorting} from 'features/core/shared/misc/sorting';
import {QlService} from 'features/core/shared/ql/service/ql.service';
import {QlPipeSettings, QlQueryResult} from 'features/core/shared/ql/builder/ql-query-builder';
import {ProvisionRunStatus} from '../../enum/provision-run-status.enum';
import {ProvisionContractRunStatus} from '../../enum/provision-contract-run-status.enum';
import {ProvisionMessageSeverity} from '../../enum/provision-message-severity.enum';
import {ProvisionTransactionStatus} from '../../enum/provision-transaction-status.enum';
import {ProvisionRunKpiDto, ProvisionRunKpiResponseDto} from '../../model/dto/api/provision-run-kpi.dto';
import {CpProvisionRunService} from '../api/cp-provision-run.service';
import {CpRunActivationSummary} from '../../interface/cp-run-activation-summary.interface';
import {CpRunActivationCounts} from '../../interface/cp-run-activation-counts.interface';

/**
 * Reads the {@link CpRunActivationSummary} of ONE provision run: the facts the activation
 * confirmation dialog states and the error figures of the step bar's "Fehler" column.
 *
 * Two request stages; a failed stage yields `null` figures, never a broken dialog:
 *
 *   1. ONE batched QL request (QL is never combined with forkJoin):
 *      - five `includeCount` pipes (paged to one row each, the same shape as the tab counters of
 *        the run detail) — bookings to activate, ERROR contracts, open ERROR messages on contract
 *        and run level, open M-026 warnings;
 *      - for the dialog only, an Envers history read (`audit: true`) of the run row, from which the
 *        last revision that CHANGED the status to CALCULATED gives "Zuletzt berechnet am". The run
 *        entity carries no calculation timestamp of its own; `lastModifiedDate` of the live row is
 *        not one either (the review transition and any edit move it).
 *      Counts and history travel in the same request and therefore succeed or fail together.
 *   2. chained after it (switchMap): the KPI endpoint `GET /mvsa/cp/provisionRuns/kpis` for the money
 *      figures. The FE does not aggregate booking amounts itself (DA-020.7): the sign of a booking
 *      lives on its transaction TYPE (booking kind + account kind), so a client-side sum would have
 *      to replicate the backend's aggregation. The endpoint only covers the LATEST run of each
 *      provision type — which an activatable run is in practice (the next run of a type is released
 *      only by "Abschließen") — and for any other run the amounts stay `null`.
 */
@Injectable({
    providedIn: 'root'
})
export class CpRunActivationSummaryService {

    /** Message code of the negative-amount rule (documentation.md, message catalogue M-026). */
    static readonly MESSAGE_CODE_NEGATIVE_AMOUNT: string = 'M-026';

    /** Upper bound of run revisions read — a run changes status a handful of times. */
    private static readonly HISTORY_PAGE_SIZE: number = 200;

    constructor(
        protected qlService: QlService,
        protected provisionRunService: CpProvisionRunService) {
    }

    /**
     * Everything the activation dialog shows.
     *
     * @param provisionRunId the run about to be activated
     */
    getActivationSummary(provisionRunId: number): Observable<CpRunActivationSummary> {

        return this.executeFigures(provisionRunId, true).pipe(
            map((result: QlQueryResult) => ({
                counts: this.toCounts(result),
                lastCalculatedAt: this.toLastCalculatedAt(result)
            })),
            catchError(error => {
                console.error('Error loading the activation figures of provision run ' + provisionRunId, error);
                return of({counts: this.emptyCounts(), lastCalculatedAt: null as string | null});
            }),
            switchMap(figures => this.getRunKpi(provisionRunId).pipe(
                map(kpi => ({
                    provisionRunId: provisionRunId,
                    ...figures.counts,
                    calculatedProvisionAmount: kpi?.calculatedProvisionAmount ?? null,
                    reserveContributionAmount: kpi?.reserveContributionAmount ?? null,
                    lastCalculatedAt: figures.lastCalculatedAt
                }))
            ))
        );
    }

    /**
     * Only the counts (one QL request) — what the step bar needs for its "Fehler" column.
     *
     * @param provisionRunId the run shown in the step bar
     */
    getCounts(provisionRunId: number): Observable<CpRunActivationCounts> {

        if (!provisionRunId) {
            return of(this.emptyCounts());
        }

        return this.executeFigures(provisionRunId, false).pipe(
            map((result: QlQueryResult) => this.toCounts(result)),
            catchError(error => {
                console.error('Error loading the activation counts of provision run ' + provisionRunId, error);
                return of(this.emptyCounts());
            })
        );
    }

    /**
     * The one batched QL request behind both public methods: the five count queries, plus the Envers
     * history of the run when `withHistory` is set (the step bar does not need it).
     */
    private executeFigures(provisionRunId: number, withHistory: boolean): Observable<QlQueryResult> {

        const counts = this.qlService.builder()
            .showErrors(false)
            .query({
                name: 'activationBookings',
                type: 'view',
                start: {type: 'entity', name: 'cp.ProvisionContractTransaction', as: 'e'},
                filters: [
                    FilterCriteria.create('e.provisionRunId', FilterCriteria.cOperatorEqual, provisionRunId),
                    FilterCriteria.create('e.status', FilterCriteria.cOperatorEqual,
                        ProvisionTransactionStatus.CALCULATED)
                ],
                paging: PagingDto.createSize(1)
            })
            .query({
                name: 'activationErrorContracts',
                type: 'view',
                start: {type: 'entity', name: 'cp.ProvisionContractRun', as: 'e'},
                filters: [
                    FilterCriteria.create('e.provisionRunId', FilterCriteria.cOperatorEqual, provisionRunId),
                    FilterCriteria.create('e.status', FilterCriteria.cOperatorEqual, ProvisionContractRunStatus.ERROR)
                ],
                paging: PagingDto.createSize(1)
            })
            .query({
                name: 'activationErrorMessages',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'cp.ProvisionContractRunMessage',
                    as: 'e',
                    joins: [{type: 'entity', name: 'provisionContractRun', as: 'pcr', joinType: 'inner'}]
                },
                filters: [
                    FilterCriteria.create('pcr.provisionRunId', FilterCriteria.cOperatorEqual, provisionRunId),
                    FilterCriteria.create('e.obsolete', FilterCriteria.cOperatorEqual, false),
                    FilterCriteria.create('e.severity', FilterCriteria.cOperatorEqual, ProvisionMessageSeverity.ERROR)
                ],
                paging: PagingDto.createSize(1)
            })
            .query({
                name: 'activationRunErrorMessages',
                type: 'view',
                start: {type: 'entity', name: 'cp.ProvisionRunMessage', as: 'e'},
                filters: [
                    FilterCriteria.create('e.provisionRunId', FilterCriteria.cOperatorEqual, provisionRunId),
                    FilterCriteria.create('e.obsolete', FilterCriteria.cOperatorEqual, false),
                    FilterCriteria.create('e.severity', FilterCriteria.cOperatorEqual, ProvisionMessageSeverity.ERROR)
                ],
                paging: PagingDto.createSize(1)
            })
            .query({
                name: 'activationM026Warnings',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'cp.ProvisionContractRunMessage',
                    as: 'e',
                    joins: [{type: 'entity', name: 'provisionContractRun', as: 'pcr', joinType: 'inner'}]
                },
                filters: [
                    FilterCriteria.create('pcr.provisionRunId', FilterCriteria.cOperatorEqual, provisionRunId),
                    FilterCriteria.create('e.obsolete', FilterCriteria.cOperatorEqual, false),
                    FilterCriteria.create('e.messageCode', FilterCriteria.cOperatorEqual,
                        CpRunActivationSummaryService.MESSAGE_CODE_NEGATIVE_AMOUNT)
                ],
                paging: PagingDto.createSize(1)
            })
            .pipe(this.countPipe('activationBookings'))
            .pipe(this.countPipe('activationErrorContracts'))
            .pipe(this.countPipe('activationErrorMessages'))
            .pipe(this.countPipe('activationRunErrorMessages'))
            .pipe(this.countPipe('activationM026Warnings'));

        if (!withHistory) {
            return counts.execute();
        }

        // Read without a status filter and matched in memory (toLastCalculatedAt), so the comparison
        // works whether the audit pipe returns the enum ordinal or its name.
        return counts
            .query({
                name: 'activationRunHistory',
                type: 'view',
                audit: true,
                start: {type: 'entity', name: 'cp.ProvisionRun', as: 'e'},
                fixedFilters: [FilterCriteria.create('e.id', FilterCriteria.cOperatorEqual, provisionRunId)],
                sortings: [new Sorting('e.rev', false)],
                selectAttributes: ['e.id', 'e.rev', 'e.status', 'e.statusMod', 'e.lastModifiedDate'],
                paging: PagingDto.createSize(CpRunActivationSummaryService.HISTORY_PAGE_SIZE)
            })
            .pipe({name: 'activationRunHistory', simpleQuery: 'activationRunHistory'})
            .execute();
    }

    private toCounts(result: QlQueryResult): CpRunActivationCounts {
        return {
            calculatedBookingCount: result.getCount('activationBookings'),
            errorContractCount: result.getCount('activationErrorContracts'),
            openErrorMessageCount: result.getCount('activationErrorMessages'),
            openRunErrorMessageCount: result.getCount('activationRunErrorMessages'),
            openM026WarningCount: result.getCount('activationM026Warnings')
        };
    }

    /**
     * End of the last finished calculation: the newest revision of the run row in which the status
     * CHANGED (`statusMod`) to CALCULATED.
     */
    private toLastCalculatedAt(result: QlQueryResult): string | null {
        const revision = (result.get('activationRunHistory') ?? []).find((entry: any) =>
            entry['e.statusMod'] === true && this.isCalculated(entry['e.status']));
        return revision?.['e.lastModifiedDate'] ?? null;
    }

    private emptyCounts(): CpRunActivationCounts {
        return {
            calculatedBookingCount: null,
            errorContractCount: null,
            openErrorMessageCount: null,
            openRunErrorMessageCount: null,
            openM026WarningCount: null
        };
    }

    /**
     * The KPI tile of this run, or `null` when the run is not the latest of its provision type (the
     * endpoint aggregates nothing else) or the endpoint failed.
     */
    private getRunKpi(provisionRunId: number): Observable<ProvisionRunKpiDto | null> {

        return this.provisionRunService.getKpis().pipe(
            map((response: ProvisionRunKpiResponseDto) =>
                (response?.provisionTypes ?? [])
                    .map(type => type.latestProvisionRun)
                    .find(run => run?.provisionRunId === provisionRunId) ?? null),
            catchError(error => {
                console.error('Error loading the provision KPIs for run ' + provisionRunId, error);
                return of(null);
            })
        );
    }

    /** A pipe that answers only the total row count of its query. */
    private countPipe(name: string): QlPipeSettings {
        return {name: name, simpleQuery: name, settings: {includeCount: true}};
    }

    private isCalculated(status: unknown): boolean {
        return status === ProvisionRunStatus.CALCULATED
            || status === ProvisionRunStatus[ProvisionRunStatus.CALCULATED];
    }
}
